import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMessageCircle, FiCheckSquare, FiMessageSquare, FiZap, FiAward } from 'react-icons/fi';
import { NewConversationButton } from '../common/NewConversationButton';

interface LaunchPadProps {
  className?: string;
}

type ActionDef =
  | { kind: 'route'; label: string; path: string; icon: React.ReactNode; gradient: string; glowColor: string }
  | { kind: 'picker'; label: string; icon: React.ReactNode; gradient: string; glowColor: string };

const ACTIONS: ActionDef[] = [
  {
    kind: 'route',
    label: 'Strategy Chat',
    path: '/work/chat',
    icon: <FiMessageCircle className="w-3.5 h-3.5" />,
    gradient: 'from-blue-600 to-indigo-600',
    glowColor: 'rgba(99,102,241,0.3)',
  },
  {
    kind: 'route',
    label: 'New Task',
    path: '/tasks',
    icon: <FiCheckSquare className="w-3.5 h-3.5" />,
    gradient: 'from-violet-600 to-purple-600',
    glowColor: 'rgba(139,92,246,0.3)',
  },
  {
    kind: 'picker',
    label: 'New Conversation',
    icon: <FiMessageSquare className="w-3.5 h-3.5" />,
    gradient: 'from-blue-600 to-cyan-600',
    glowColor: 'rgba(59,130,246,0.3)',
  },
  {
    kind: 'route',
    label: 'Forge Persona',
    path: '/personas/forge',
    icon: <FiZap className="w-3.5 h-3.5" />,
    gradient: 'from-purple-600 to-violet-600',
    glowColor: 'rgba(147,51,234,0.3)',
  },
  {
    kind: 'route',
    label: 'Enter Arena',
    path: '/gymnasium/arena',
    icon: <FiAward className="w-3.5 h-3.5" />,
    gradient: 'from-amber-600 to-orange-600',
    glowColor: 'rgba(245,158,11,0.3)',
  },
];

/** One styled pill, shared by route actions and the picker trigger so all five look identical. */
const ActionButton = (
  action: ActionDef,
  onClick: () => void,
  picker?: { isOpen: boolean },
) => (
  <button
    key={action.label}
    onClick={onClick}
    {...(picker ? { 'aria-haspopup': 'listbox' as const, 'aria-expanded': picker.isOpen } : {})}
    className={`
      w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-white
      bg-gradient-to-r ${action.gradient}
      motion-safe:hover:scale-[1.02] motion-safe:hover:translate-y-[-1px]
      active:scale-[0.99]
      transition-all duration-200
      relative overflow-hidden
    `}
    style={{ boxShadow: `0 2px 8px ${action.glowColor}` }}
  >
    {/* Hatching overlay */}
    <div
      className="absolute inset-0 opacity-[0.04] pointer-events-none"
      style={{ backgroundImage: 'repeating-linear-gradient(45deg, #fff, #fff 1px, transparent 1px, transparent 8px)' }}
    />
    <span className="relative z-10 flex items-center gap-2">
      {action.icon}
      {action.label}
    </span>
  </button>
);

const QuickActions: React.FC<LaunchPadProps> = ({ className }) => {
  const navigate = useNavigate();

  return (
    <div className={`space-y-1.5 ${className || ''}`}>
      {ACTIONS.map(action =>
        action.kind === 'picker' ? (
          <NewConversationButton
            key={action.label}
            renderTrigger={({ open, isOpen }) => ActionButton(action, open, { isOpen })}
          />
        ) : (
          ActionButton(action, () => navigate(action.path))
        ),
      )}
    </div>
  );
};

export default QuickActions;
